import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from '@legendapp/state/react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Plus, Search, SlidersHorizontal } from 'lucide-react';
import { ICON_SIZE } from '../../design-system';
import { getListArticleCounts, getListById, openUploadModal } from '../../store';
import ListSwitcherSheet from './ListSwitcherSheet';
import ViewSortSheet from './ViewSortSheet';
import './MobileTopBar.css';

/**
 * Phone top bar. Identity and navigation live here because you touch them once
 * a session; everything repeated sits in the thumb zone instead.
 *
 * The list name is the switcher target, not a label — it is where the desktop
 * sidebar's list tree goes.
 */
const MobileTopBar = () => {
  const { t } = useTranslation(['sidebar', 'common', 'articles']);
  const { pathname } = useLocation();
  const { listId } = useParams();
  const navigate = useNavigate();
  const [openSheet, setOpenSheet] = useState(null);

  const list = useSelector(() => (listId ? getListById(listId) : null));
  const listName = listId
    ? (list?.name ?? t('common:loading'))
    : t(pathname === '/all' ? 'sidebar:all' : 'sidebar:inbox');

  const countKey = listId ?? (pathname === '/all' ? 'all' : 'inbox');
  const listCount = useSelector(() => getListArticleCounts()[countKey] ?? 0);

  return (
    <header className="mobile-top-bar">
      <button
        type="button"
        className="mobile-top-bar__switcher"
        onClick={() => setOpenSheet('lists')}
        aria-haspopup="dialog"
        aria-expanded={openSheet === 'lists'}
        data-testid="mobile-list-switcher"
      >
        <span className="mobile-top-bar__list-name">{listName}</span>
        <ChevronDown size={ICON_SIZE.md} aria-hidden="true" />
        {/* Not aria-hidden: it is visible text inside the control, so hiding it
            would leave the accessible name missing part of the visible label. */}
        <span className="mobile-top-bar__count">{listCount}</span>
      </button>

      <button
        type="button"
        className="mobile-top-bar__action"
        onClick={() => setOpenSheet('view')}
        aria-haspopup="dialog"
        aria-expanded={openSheet === 'view'}
        aria-label={t('articles:mobile.viewAndSort')}
        data-testid="mobile-view-sort"
      >
        <SlidersHorizontal size={ICON_SIZE.lg} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="mobile-top-bar__action"
        onClick={() => navigate('/search')}
        aria-label={t('common:search')}
        data-testid="mobile-search-open"
      >
        <Search size={ICON_SIZE.lg} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="mobile-top-bar__action"
        data-onboarding="import"
        onClick={openUploadModal}
        aria-label={t('common:layout.uploadOrImport')}
      >
        <Plus size={ICON_SIZE.lg} aria-hidden="true" />
      </button>

      <ListSwitcherSheet
        isOpen={openSheet === 'lists'}
        onClose={() => setOpenSheet(null)}
      />
      <ViewSortSheet
        isOpen={openSheet === 'view'}
        onClose={() => setOpenSheet(null)}
        listId={listId}
      />
    </header>
  );
};

export default MobileTopBar;
